import {
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo
} from 'react';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { debounce } from '@thd-olt-functional/utils';
import { getInitialMessage } from '../shared/utils/session-utils';
import { checkGenerateQuestionsNeeded } from '../shared/utils/suggestions';
import { announceAssistantReady } from '../platform/events/lifecycle';
import { logger } from '../shared/utils/logger';
// IndexedDB-backed message store. See .claude/context/persistence.md
import { useMessageStore } from '../platform/persistence/messageStore';
import { conversationRecord, isNewSessionGeneration } from '../platform/session/session';
import { FLAGS } from '../platform/config/flags';
import { isCustomerTypeAllowed } from '../platform/config/surface';
import { CHANNELS, createBroadcastChannel } from '../platform/events/broadcast';
import { reconcileMessages } from '../shared/utils/message-reconcile';
import { normalizeLegacyMessageType } from '../shared/message';
import { noticeError } from '../platform/telemetry/newRelic';

/**
 * @param { boolean } withPersistence - Flag to check if persistence is desired
 * @param { Object } session
 * @param { { getState: () => object } } [assistantStore] - the assistant store. When passed, this
 *   hook publishes its history and ISQ state into it. Not the persistence `store` below.
 * @param { Function } [submitGenerateQuestions] - the query layer's ISQ trigger capability.
 * @param { boolean } [enabled] - false while the holder has no query layer: init never runs, so
 *   nothing is pruned, cleared or seeded in assistantBotDB and readiness is not announced.
 *   Flips false → true at most once. See .claude/context/message-history.md.
 * @returns { UseMessageHistoryReturn }
 */
export const useMessageHistory = (
  withPersistence = true, session, assistantStore, submitGenerateQuestions = () => {}, enabled = true,
) => {
  const {
    isConsumerApp,
    channel,
    customer,
    deliveryZip
  } = useContext(ExperienceContext);

  const isDynamicIsqMasterEnabled = useConfigService(FLAGS.dynamicIsqMasterEnabled);
  const isDynamicIsqDtFsEnabled = useConfigService(FLAGS.dynamicIsqDtEnabled);
  const isDynamicIsqMwFsEnabled = useConfigService(FLAGS.dynamicIsqMwEnabled);
  const isDynamicIsqAppFsEnabled = useConfigService(FLAGS.dynamicIsqAppEnabled);
  const isDynamicIsqB2BEnabled = useConfigService(FLAGS.dynamicIsqB2bEnabled);
  const isDynamicIsqValidCustomerType = isCustomerTypeAllowed(customer?.type, isDynamicIsqB2BEnabled);

  const maBroadcastChannel = useRef(null);
  const isStorageAvailable = useRef(false);
  const hasHistoryInitialized = useRef(false);
  const initializationLock = useRef(false);
  const prevCustomerType = useRef(customer?.type);
  const [messages, setMessages] = useState([getInitialMessage(session.sessionId)]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCallGeneratedQuestions, setCallToGenerateQuestions] = useState(false);

  // Scoped to this holder's session: a tab open across a session rotation must not render
  // a peer tab's conversation. See .claude/context/persistence.md
  const store = useMessageStore(session.sessionId);

  // Storage is best-effort: a failure is reported, never left unhandled, and never allowed to
  // stop the conversation. See .claude/context/message-history.md ("Storage is best-effort").
  const reportStorageFailure = (maaOperation) => (error) => noticeError(error, { maaOperation });

  const loadIDBMessages = () => {
    setIsLoading(true);
    return store.getAll()
      .then((savedMessages) => {
        // Coerce legacy persisted types (uppercase 'AGENT' → 'agent') on read, before
        // sort/reconcile, so rows written before the casing normalization still render.
        const normalized = savedMessages.map(normalizeLegacyMessageType);
        const sortedMessages = [...normalized].sort((first, second) => {
          if (first.isInitialMessage) return -1;
          if (second.isInitialMessage) return 1;
          return 0;
        });
        setMessages((prev) => reconcileMessages(prev, sortedMessages));
        return normalized;
      })
      // Also on failure: the composer treats history loading as busy.
      .finally(() => setIsLoading(false));
  };

  const updateMessage = async (message, updates, reload = false) => {
    /* global structuredClone */
    const updatedMessage = { ...structuredClone(message), ...updates, updatedAt: Date.now() };

    await store.update(updatedMessage);
    if (reload) await loadIDBMessages();
  };

  const broadCastMessageUpdate = () => {
    if (maBroadcastChannel.current) {
      maBroadcastChannel.current.postMessage('magic-apron-new-message');
    }
  };

  // `waitForStorage: false` resolves at once with the stamped message; the write finishes later.
  // See .claude/context/orchestrator-flows.md ("The request never waits for storage").
  const saveMessage = async (newMessage, { waitForStorage = true } = {}) => {
    // PII redaction on persisted agent-mode messages is deliberately unbuilt (WS-8.3) —
    // see .claude/context/message-history.md before implementing it.
    const updatedMessage = {
      ...newMessage,
      sessionId: session.sessionId,
      feedbackProvided: false,
      timestamp: Date.now(),
      updatedAt: Date.now(),
    };
    if (!isStorageAvailable.current) {
      // Functional updater — closing over `messages` dropped saves landed in the same tick.
      setMessages((curr) => [...curr, updatedMessage]);
      return Promise.resolve(updatedMessage);
    }
    if (!session.sessionId) return Promise.reject(new Error('Session ID not available'));

    // Resolve with THIS message, never a row read back: other web views write to the same
    // store, so the newest row may not be ours. A failed write keeps it in memory instead.
    const written = store.add(updatedMessage)
      .then(loadIDBMessages)
      .then(() => updatedMessage)
      .catch((error) => {
        reportStorageFailure('saveMessage')(error);
        setMessages((curr) => [...curr, updatedMessage]);
        return updatedMessage;
      });
    return waitForStorage ? written : updatedMessage;
  };

  /**
   * Cross-tab safe `saveMessage`. Resolves to the saved message, or `null` when a peer
   * tab already wrote the same `messageId`. See .claude/context/message-history.md
   */
  const saveMessageIfNew = async (newMessage) => {
    const updatedMessage = {
      ...newMessage,
      sessionId: session.sessionId,
      feedbackProvided: false,
      timestamp: Date.now(),
      updatedAt: Date.now(),
    };

    if (!isStorageAvailable.current) {
      if (!updatedMessage.messageId) {
        setMessages((curr) => [...curr, updatedMessage]);
        return updatedMessage;
      }
      let didAppend = false;
      setMessages((curr) => {
        if (curr.some((msg) => msg.messageId === updatedMessage.messageId)) return curr;
        didAppend = true;
        return [...curr, updatedMessage];
      });
      return didAppend ? updatedMessage : null;
    }
    if (!session.sessionId) throw new Error('Session ID not available');
    // No messageId (e.g. LIVE_AGENT_CONNECTING_MESSAGE) — nothing to dedup, plain add.
    // Resolves with this message, not a row read back (see saveMessage).
    if (!updatedMessage.messageId) {
      return store.add(updatedMessage)
        .then(loadIDBMessages)
        .then(() => updatedMessage);
    }

    // Cross-tab dedup add: the scan and the conditional insert run in one IndexedDB
    // transaction, matched on `messageId` (see idb-core#insertIfAbsent).
    const wasInserted = await store.insertIfAbsent(updatedMessage, 'messageId');
    await loadIDBMessages();
    if (wasInserted) broadCastMessageUpdate();
    return wasInserted ? updatedMessage : null;
  };

  // Delete every non-initial message (see idb-core#clearMessages).
  const clearMessageHistory = () => store
    .clearMessages({ keepInitial: true })
    .then(loadIDBMessages)
    .catch(reportStorageFailure('clearHistory'));

  const clearHistory = debounce(50, clearMessageHistory); // 50ms delay for consistency

  // Startup prune: stale rows, duplicate initials, blanked b2b suggestions
  // (see idb-core#cleanOrphans).
  const cleanIndexDb = () => store.cleanOrphans({
    sessionId: session.sessionId,
    customerType: customer?.type,
  });

  /**
   * Drop a conversation left over from a previous session generation, then re-watermark.
   * See .claude/context/message-history.md
   */
  const clearHistoryFromPreviousSession = async () => {
    const { lastSessionStart } = conversationRecord.get();
    const sessionStart = session.timestamp ?? null;

    if (isNewSessionGeneration(lastSessionStart, sessionStart)) {
      await store.clearMessages({ keepInitial: false });
    }
    if (lastSessionStart !== sessionStart) {
      conversationRecord.update({ lastSessionStart: sessionStart });
    }
  };

  const init = async () => {
    if (initializationLock.current) return;
    initializationLock.current = true;
    try {
      isStorageAvailable.current = withPersistence && typeof indexedDB !== 'undefined';
      // Before the prune and the initial-message check below, so a cleared history re-seeds
      // its opening message and re-runs the suggested-questions decision.
      await clearHistoryFromPreviousSession();
      await cleanIndexDb();

      let savedMessages = await store.getAll();
      const initialMessage = savedMessages.find((msg) => msg.isInitialMessage);

      if (!initialMessage) {
        await store.add({ ...getInitialMessage(session.sessionId), updatedAt: Date.now() });
      }
      // Retrieving get all messages again to ensure we have the initial message
      savedMessages = await store.getAll();
      const shouldCallGenerateQuestions = checkGenerateQuestionsNeeded({
        savedMessages,
        channel,
        isConsumerApp,
        isDynamicIsqMasterEnabled,
        isDynamicIsqDtFsEnabled,
        isDynamicIsqMwFsEnabled,
        isDynamicIsqAppFsEnabled,
        isDynamicIsqValidCustomerType
      });
      if (shouldCallGenerateQuestions) {
        setCallToGenerateQuestions(true);

      }

      hasHistoryInitialized.current = true;
      // Deliberately the ONLY announcement point — see .claude/context/event-bus.md
      // ("Never re-announce off drawer traffic").
      announceAssistantReady();

      await loadIDBMessages();
    } finally {
      initializationLock.current = false;
    }
  };

  useEffect(() => {
    // `legacy`: assistantBotDB and readiness belong to its package. See
    // .claude/context/message-history.md.
    if (!enabled) return undefined;
    if (typeof BroadcastChannel !== 'undefined') {
      maBroadcastChannel.current = createBroadcastChannel(CHANNELS.newMessage);
    }

    init()
      .then(() => {
        if (maBroadcastChannel.current) {
          maBroadcastChannel.current.onmessage = () => {
            logger.log('Received message update broadcast, reloading messages from IndexedDB', messages);
            loadIDBMessages().catch(reportStorageFailure('reloadMessages'));
          };
        }
      })
      .catch(reportStorageFailure('init'));

    return (() => {
      if (maBroadcastChannel.current) {
        maBroadcastChannel.current.close();
        maBroadcastChannel.current = null;
      }
      initializationLock.current = false;
    });
  }, [enabled]);

  // This hook decides to ask; the query layer issues the query — see
  // .claude/context/message-history.md.
  useEffect(() => {
    if (isCallGeneratedQuestions) {
      submitGenerateQuestions({ isConsumerApp, channel, deliveryZip });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCallGeneratedQuestions]);

  /**
   * Patch the opening message with the generated suggested questions. The query layer calls
   * this when `generateQuestions` resolves. See .claude/context/message-history.md
   *
   * @param {object} response  raw `generateQuestions` data.
   */
  const commitSuggestedQuestions = async (response) => {
    try {
      const dbMessages = await store.getAll();
      const initialMessage = dbMessages.find(({ isInitialMessage }) => isInitialMessage);
      if (!initialMessage) return;

      initialMessage.content.relatedQuestions = {
        suggestedQuestions: response?.generateQuestions?.suggestedQuestions || [],
        followUpQuestions: [],
        clarifyingQuestions: []
      };
      initialMessage.updatedAt = Date.now();

      await store.update(initialMessage)
        .then(loadIDBMessages)
        .finally(() => { broadCastMessageUpdate(); });
      setCallToGenerateQuestions(false);
    } catch (error) {
      // The query layer calls this without awaiting it.
      reportStorageFailure('commitSuggestedQuestions')(error);
    }
  };

  useEffect(() => {
    const prev = prevCustomerType.current;
    prevCustomerType.current = customer?.type;

    if (prev !== 'b2b' && customer?.type === 'b2b') {
      if (isStorageAvailable.current) {
        store.getAll().then((dbMessages) => {
          const initialMessage = dbMessages.find(({ isInitialMessage }) => isInitialMessage);
          if (!initialMessage?.content?.relatedQuestions?.suggestedQuestions?.length) return undefined;
          initialMessage.content.relatedQuestions.suggestedQuestions = [];
          initialMessage.updatedAt = Date.now();
          return store.update(initialMessage).then(loadIDBMessages).then(broadCastMessageUpdate);
        }).catch(reportStorageFailure('clearB2bSuggestions'));
      } else {
        setMessages((current) => current.map((msg) => (
          // eslint-disable-next-line max-len
          msg.isInitialMessage ? { ...msg, content: { ...msg.content, relatedQuestions: { suggestedQuestions: [], followUpQuestions: [], clarifyingQuestions: [] } } } : msg
        )));
      }
    }
  }, [customer?.type]);

  const { lastBotMessage, lastUserMessage } = useMemo(() => ({
    lastBotMessage: messages.findLast(({ type }) => type === 'bot'),
    lastUserMessage: messages.findLast(({ type }) => type === 'user'),
  }), [messages]);

  // Publishes state directly into the assistant store — see
  // .claude/context/message-history.md.
  const historyInitialized = hasHistoryInitialized.current;
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setMessages(messages);
  }, [messages, assistantStore]);
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setLastBotMessage(lastBotMessage);
  }, [lastBotMessage, assistantStore]);
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setLastUserMessage(lastUserMessage);
  }, [lastUserMessage, assistantStore]);
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setHistoryLoading(isLoading);
  }, [isLoading, assistantStore]);
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setWereQuestionsGenerated(historyInitialized);
  }, [historyInitialized, assistantStore]);

  return {
    messages,
    lastBotMessage,
    lastUserMessage,
    saveMessage,
    saveMessageIfNew,
    historyLoading: isLoading,
    hasHistoryInitialized: hasHistoryInitialized.current,
    clearHistory,
    updateMessage,
    broadCastMessageUpdate,
    // Preserve the hook's public shape for consumers.
    iDBGetAll_messages: store.getAll,
    iDBUpdate_message: store.update,
    loadIDBMessages,
    // Driven by the query-layer block when its generateQuestions operation resolves.
    commitSuggestedQuestions,
  };
};
